import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const GA_MEASUREMENT_ID = 'G-S631RRENTF';

// Enable GA4 tracking
const enableTracking = () => {
  if (typeof window.gtag !== 'function') return;
  
  // Update consent to granted
  window.gtag('consent', 'update', {
    'analytics_storage': 'granted',
    'ad_storage': 'granted'
  });
  
  // Send initial page view
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
  });
};

// Disable GA4 tracking
const disableTracking = () => {
  if (typeof window.gtag !== 'function') return;
  
  window.gtag('consent', 'update', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied'
  });
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    
    if (consent === 'accepted') {
      // User previously accepted - enable tracking
      enableTracking();
    } else if (!consent) {
      // No consent yet - show banner after delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
    // If declined, tracking stays disabled (default state)
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookie-consent", "accepted");
    enableTracking();
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem("cookie-consent", "declined");
    disableTracking();
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
        >
          <div className="max-w-7xl mx-auto">
            <div className="bg-[#0E0F14] border border-white rounded-lg p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg mb-2">
                    Vi bruger cookies 🍪
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Vi bruger cookies til at analysere trafik, forbedre din oplevelse og forstå 
                    købsadfærd på vores website. Ved at klikke "Accepter" giver du samtykke til 
                    vores brug af cookies. Læs mere i vores{" "}
                    <Link to="/privatlivspolitik" className="text-white underline hover:text-white/80">
                      privatlivspolitik
                    </Link>.
                  </p>
                </div>
                <div className="flex gap-3 shrink-0">
                  <Button
                    onClick={declineCookies}
                    className="bg-[#1A1C23] text-white border border-white hover:bg-[#1A1C23]/80 hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(255,255,255,0.15)] transition-all"
                  >
                    Afvis
                  </Button>
                  <Button
                    onClick={acceptCookies}
                    className="bg-[#1A1C23] text-white border border-white hover:bg-[#1A1C23]/80 hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(255,255,255,0.15)] transition-all"
                  >
                    Accepter cookies
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
